import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { fileUrl } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import {
  Heart, ShoppingBag, Share2, RotateCw, Radio, MessageCircle, Bookmark, Plus, Check, LocateFixed, MapPin,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import CommentsSheet from "../components/CommentsSheet";
import StoriesStrip from "../components/StoriesStrip";
import VerifiedBadge from "../components/VerifiedBadge";
import UserGem from "../components/UserGem";
import LiveRail from "../components/LiveRail";
import { SalePill } from "../components/SalePill";
import FoundingVendorBadge from "../components/FoundingVendorBadge";
import { getActiveSale, effectivePrice } from "../lib/promo";

/** Parse hashtags into tappable pink spans. */
function HashtagText({ text, onTagClick }) {
  if (!text) return null;
  const parts = text.split(/(\s+)/);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("#") ? (
          <span
            key={i}
            onClick={() => onTagClick?.(p.slice(1))}
            className="text-pink-300 font-bold hover:underline cursor-pointer"
          >
            {p}
          </span>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
}

function FormatCount(n) {
  if (n == null) return "0";
  if (n < 1000) return String(n);
  if (n < 1000000) return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}K`;
  return `${(n / 1000000).toFixed(1)}M`;
}

function ActionButton({ icon: Icon, label, onClick, active, activeClass = "bg-pink-500", testid, fillWhenActive = true }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1 group"
      data-testid={testid}
      aria-label={label}
    >
      <span
        className={`w-12 h-12 grid place-items-center rounded-full transition-all ${
          active
            ? `${activeClass} text-white glow-pink`
            : "bg-black/40 backdrop-blur-md border border-white/15 text-white group-hover:bg-white/10"
        }`}
      >
        <Icon className={`w-6 h-6 ${active && fillWhenActive ? "fill-white" : ""}`} strokeWidth={2.2} />
      </span>
      <span className="text-[10px] text-white font-semibold drop-shadow">{label}</span>
    </button>
  );
}

function VendorBlock({ product, following, onFollowToggle, onOpenVendor }) {
  return (
    <div className="relative mb-4 flex items-center justify-center">
      <button
        onClick={onOpenVendor}
        className="relative w-12 h-12 rounded-full grid place-items-center overflow-hidden border-2 border-white"
        data-testid={`feed-vendor-avatar-${product.product_id}`}
        aria-label={`Open @${product.vendor_name}`}
      >
        {product.vendor_avatar ? (
          <img src={fileUrl(product.vendor_avatar)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-pink-500 to-rose-600 grid place-items-center text-white font-black text-lg">
            {(product.vendor_name || "?")[0]}
          </div>
        )}
      </button>
      <button
        onClick={onFollowToggle}
        className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full grid place-items-center text-white shadow-md transition-all border-2 border-black ${
          following ? "bg-zinc-700" : "bg-pink-500 hover:bg-pink-400 pulse-glow"
        }`}
        data-testid={`feed-follow-${product.product_id}`}
        aria-label={following ? "Unfollow" : "Follow"}
      >
        {following ? <Check className="w-2.5 h-2.5" /> : <Plus className="w-2.5 h-2.5" />}
      </button>
    </div>
  );
}

function ProductSlide({ product, onLike, onAdd, onShare, onOpenVendor, onFollow, onSave, onComment, onDoubleTapLike, liveStream, isVisible }) {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [showHeart, setShowHeart] = useState(0);
  const lastTapRef = useRef(0);

  const img = product.image_paths?.[0];
  const video = product.video_paths?.[0];
  const heroUrl = img
    ? fileUrl(img)
    : "https://images.pexels.com/photos/8879373/pexels-photo-8879373.jpeg";

  // Video play/pause based on visibility
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isVisible) {
      v.play().catch(() => {});
    } else {
      v.pause();
      v.currentTime = 0;
    }
  }, [isVisible]);

  // Progress bar tick for video
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      if (v.duration) setProgress((v.currentTime / v.duration) * 100);
    };
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, []);

  // Double-tap to like
  const handleTap = (e) => {
    const now = Date.now();
    if (now - lastTapRef.current < 350) {
      e.preventDefault();
      onDoubleTapLike?.();
      setShowHeart(now);
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  };

  return (
    <article
      ref={containerRef}
      onClick={handleTap}
      className="relative w-full h-[100dvh] overflow-hidden bg-black select-none"
      data-testid={`feed-slide-${product.product_id}`}
    >
      {/* Hero — video or static image, full-bleed but never cropped.
          A blurred copy of the media fills the remaining space so the actual
          uploaded media is shown clean (object-contain) without zoom or cropping. */}
      {video ? (
        <>
          <div
            className="absolute inset-0 bg-black"
            style={{
              backgroundImage: heroUrl ? `url(${heroUrl})` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(32px) brightness(0.55)",
              transform: "scale(1.15)",
            }}
          />
          <video
            ref={videoRef}
            src={fileUrl(video)}
            poster={heroUrl}
            className="absolute inset-0 w-full h-full object-contain"
            muted
            loop
            playsInline
            preload="metadata"
            data-testid={`feed-video-${product.product_id}`}
          />
        </>
      ) : (
        <>
          <div
            className="absolute inset-0 bg-black"
            style={{
              backgroundImage: heroUrl ? `url(${heroUrl})` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(32px) brightness(0.55)",
              transform: "scale(1.15)",
            }}
          />
          <img
            src={heroUrl}
            alt={product.name}
            className="absolute inset-0 w-full h-full object-contain"
            loading="lazy"
          />
        </>
      )}
      <div className="absolute inset-0 feed-overlay" />

      {/* LIVE pill */}
      {liveStream && (
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/live/${liveStream.stream_id}`); }}
          className="absolute top-20 left-4 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500 text-white text-xs font-black tracking-widest pulse-glow"
          data-testid={`feed-live-badge-${product.product_id}`}
        >
          <Radio className="w-3 h-3" /> LIVE NOW
        </button>
      )}

      {/* Right action sidebar */}
      <div className="absolute right-3 bottom-32 z-10 flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
        <VendorBlock product={product} following={product.following_vendor} onFollowToggle={onFollow} onOpenVendor={onOpenVendor} />

        <ActionButton
          icon={Heart}
          label={FormatCount(product.likes || 0)}
          onClick={onLike}
          active={product.liked}
          testid={`like-btn-${product.product_id}`}
        />
        <ActionButton
          icon={MessageCircle}
          label={FormatCount(product.comment_count || 0)}
          onClick={onComment}
          testid={`comment-btn-${product.product_id}`}
          fillWhenActive={false}
        />
        <ActionButton
          icon={Bookmark}
          label="Save"
          onClick={onSave}
          active={product.saved}
          activeClass="bg-amber-400"
          testid={`save-btn-${product.product_id}`}
        />
        <ActionButton
          icon={ShoppingBag}
          label="Bag"
          onClick={onAdd}
          testid={`add-cart-btn-${product.product_id}`}
          fillWhenActive={false}
        />
        <ActionButton
          icon={Share2}
          label="Share"
          onClick={onShare}
          testid={`share-btn-${product.product_id}`}
          fillWhenActive={false}
        />
      </div>

      {/* Bottom info card */}
      <div className="absolute bottom-24 left-0 right-20 px-5 z-10" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onOpenVendor}
          className="flex items-center gap-1.5 mb-2 text-white"
          data-testid={`vendor-handle-${product.product_id}`}
        >
          <span className="text-[15px] font-black drop-shadow">@{product.vendor_name}</span>
          <VerifiedBadge verified={product.vendor_verified} size="sm" />
          <UserGem userId={product.vendor_id} size="sm" />
          <FoundingVendorBadge userId={product.vendor_id} size="sm" />
          {!product.following_vendor && (
            <span
              onClick={(e) => { e.stopPropagation(); onFollow?.(); }}
              className="ml-1 inline-flex items-center px-2.5 py-0.5 rounded-full bg-pink-500 hover:bg-pink-400 text-white text-[11px] font-bold"
              data-testid={`vendor-follow-pill-${product.product_id}`}
            >
              Follow
            </span>
          )}
        </button>

        {/* Status badges */}
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          <SalePill product={product} />
          {product.distance_km != null && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-cyan-400/20 text-cyan-100 border border-cyan-400/40 font-black" data-testid={`badge-distance-${product.product_id}`}>
              <MapPin className="w-3 h-3" />
              {product.distance_km < 1
                ? `${Math.round(product.distance_km * 1000)} m`
                : `${product.distance_km.toFixed(1)} km`}
            </span>
          )}
          {product.is_sold_out && (
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/90 text-white font-black" data-testid={`badge-sold-out-${product.product_id}`}>Sold out</span>
          )}
          {!product.is_sold_out && product.is_limited && (
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-400 text-black font-black" data-testid={`badge-limited-${product.product_id}`}>Limited</span>
          )}
          {product.open_now === false && (
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-200 font-black" data-testid={`badge-closed-${product.product_id}`}>Closed</span>
          )}
          {product.open_now !== false && !product.is_sold_out && (
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/90 text-white font-black" data-testid={`badge-open-${product.product_id}`}>Open now</span>
          )}
          {product.pickup_available && (
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/10 text-zinc-200 border border-white/15">Pickup</span>
          )}
          {product.delivery_available && (
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/10 text-zinc-200 border border-white/15">Delivery</span>
          )}
        </div>

        <h2 className="text-2xl sm:text-3xl font-black tracking-tighter leading-tight text-white drop-shadow-lg">
          {product.name}
        </h2>
        <p className="mt-1.5 text-sm text-zinc-100/90 line-clamp-2 max-w-md leading-snug">
          <HashtagText
            text={product.description + (product.tags?.length ? "  " + product.tags.map((t) => "#" + t.replace(/^#/, "")).join(" ") : "")}
            onTagClick={(tag) => navigate(`/search?q=${encodeURIComponent(tag)}`)}
          />
        </p>

        <div className="mt-3 flex items-center gap-3">
          {(() => {
            const sale = getActiveSale(product);
            return (
              <span className="inline-flex items-baseline gap-2" data-testid={`price-${product.product_id}`}>
                <span className="text-2xl font-black text-gradient-neon">${effectivePrice(product).toFixed(2)}</span>
                {sale && (
                  <span className="text-sm text-zinc-300/80 line-through font-bold">${Number(product.price).toFixed(2)}</span>
                )}
              </span>
            );
          })()}
          <button
            onClick={onAdd}
            className="h-10 px-5 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink text-sm"
            data-testid={`buy-btn-${product.product_id}`}
          >
            Add to Bag
          </button>
        </div>
      </div>

      {/* Video progress bar */}
      {video && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10 z-10">
          <div className="h-full bg-pink-500" style={{ width: `${progress}%` }} />
        </div>
      )}

      {/* Double-tap heart burst */}
      <AnimatePresence>
        {showHeart > 0 && (
          <motion.div
            key={showHeart}
            initial={{ scale: 0.5, opacity: 0.9 }}
            animate={{ scale: 1.6, opacity: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="absolute inset-0 grid place-items-center pointer-events-none z-20"
            onAnimationComplete={() => setShowHeart(0)}
          >
            <Heart className="w-32 h-32 fill-pink-500 text-pink-500 drop-shadow-[0_0_24px_rgba(255,46,147,0.9)]" />
          </motion.div>
        )}
      </AnimatePresence>
    </article>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [liveByVendor, setLiveByVendor] = useState({});
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("foryou"); // 'foryou' | 'following' | 'nearme'
  const [visibleId, setVisibleId] = useState(null);
  const [commentsFor, setCommentsFor] = useState(null);
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [radius, setRadius] = useState(10);
  const { addItem } = useCart();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const load = useCallback(async (which = source) => {
    setLoading(true);
    try {
      if (which === "nearme") {
        if (!coords) {
          setItems([]);
          return;
        }
        const [feedRes, liveRes] = await Promise.all([
          api.get("/discover/nearby-products", {
            params: { lat: coords.lat, lng: coords.lng, radius_km: radius, limit: 60 },
          }),
          api.get("/streams/live"),
        ]);
        setItems(feedRes.data);
        const map = {};
        for (const s of liveRes.data) map[s.vendor_id] = s;
        setLiveByVendor(map);
        if (feedRes.data.length > 0) setVisibleId(feedRes.data[0].product_id);
        return;
      }
      const [feedRes, liveRes] = await Promise.all([
        api.get("/products/feed", { params: { limit: 30, source: which } }),
        api.get("/streams/live"),
      ]);
      setItems(feedRes.data);
      const map = {};
      for (const s of liveRes.data) map[s.vendor_id] = s;
      setLiveByVendor(map);
      // Auto-set first visible
      if (feedRes.data.length > 0) setVisibleId(feedRes.data[0].product_id);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [source, coords, radius]);

  useEffect(() => { load(source); /* eslint-disable-next-line */ }, [source, coords, radius]);

  const enableNearMe = () => {
    if (coords) {
      setSource("nearme");
      return;
    }
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this device");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          lat: Number(pos.coords.latitude.toFixed(6)),
          lng: Number(pos.coords.longitude.toFixed(6)),
        });
        setSource("nearme");
        setLocating(false);
      },
      (err) => {
        toast.error(err.code === 1 ? "Permission denied. Enable location in your browser." : "Couldn't get your location");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  // IntersectionObserver to detect which slide is in view
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const slides = root.querySelectorAll('[data-testid^="feed-slide-"]');
    if (slides.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio > 0.6) {
            const id = e.target.getAttribute("data-testid").replace("feed-slide-", "");
            setVisibleId(id);
          }
        }
      },
      { root, threshold: [0.6, 0.9] }
    );
    slides.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [items]);

  const onLike = async (p) => {
    try {
      const { data } = await api.post(`/products/${p.product_id}/like`);
      setItems((arr) => arr.map((it) =>
        it.product_id === p.product_id
          ? { ...it, liked: data.liked, likes: (it.likes || 0) + (data.liked ? 1 : -1) }
          : it,
      ));
    } catch {
      toast.error("Couldn't update like");
    }
  };

  const onSave = async (p) => {
    try {
      const { data } = await api.post(`/products/${p.product_id}/save`);
      setItems((arr) => arr.map((it) =>
        it.product_id === p.product_id ? { ...it, saved: data.saved } : it,
      ));
      toast.success(data.saved ? "Saved" : "Removed from saves");
    } catch {
      toast.error("Couldn't save");
    }
  };

  const onFollow = async (p) => {
    try {
      const { data } = await api.post(`/vendors/${p.vendor_id}/follow`);
      setItems((arr) => arr.map((it) =>
        it.vendor_id === p.vendor_id ? { ...it, following_vendor: data.following } : it,
      ));
      toast.success(data.following ? `Following @${p.vendor_name}` : "Unfollowed");
    } catch {
      toast.error("Couldn't follow");
    }
  };

  const onAdd = async (p) => {
    try {
      const res = await addItem(p.product_id, 1);
      if (res?.needs_customization) {
        toast.message(`Customize ${p.name}`);
      } else {
        toast.success(`${p.name} added to bag`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add to bag");
    }
  };

  const onShare = async (p) => {
    try {
      const url = `${window.location.origin}/vendor/${p.vendor_id}`;
      if (navigator.share) {
        await navigator.share({ title: p.name, text: p.description, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch { /* ignore */ }
  };

  const onComment = (p) => setCommentsFor(p);

  if (loading) {
    return (
      <div className="min-h-[100dvh] grid place-items-center bg-black">
        <div className="w-10 h-10 rounded-full border-2 border-pink-500/40 border-t-pink-500 animate-spin" />
      </div>
    );
  }

  const empty = items.length === 0;

  return (
    <div className="-mt-14 -mb-24 relative bg-black" data-testid="feed-container">
      {/* Row 2 (feed tabs) + Row 3 (Stories) — stacked sticky bar below the app header.
          The app header (row 1) is provided by <AppShell> and simplifies to
          [logo · messages · cart] on mobile so nothing overlaps. */}
      <div
        className="sticky left-0 right-0 z-30 bg-gradient-to-b from-black via-black/80 to-transparent"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 56px)" }}
        data-testid="feed-sticky-header"
      >
        <div className="flex items-center justify-center gap-1 px-1 py-2">
          {[
            { key: "foryou", label: "For You" },
            { key: "following", label: "Following" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setSource(t.key)}
              className={`px-3.5 h-8 rounded-full text-xs font-bold transition-colors ${
                source === t.key ? "bg-white text-black" : "text-white/80 hover:text-white"
              }`}
              data-testid={`feed-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
          <button
            onClick={() => (source === "nearme" ? setSource("foryou") : enableNearMe())}
            disabled={locating}
            className={`px-3 h-8 rounded-full text-xs font-bold transition-colors inline-flex items-center gap-1 ${
              source === "nearme" ? "bg-cyan-400 text-black" : "text-cyan-200 hover:text-white"
            }`}
            data-testid="feed-tab-nearme"
          >
            <LocateFixed className="w-3.5 h-3.5" />
            {locating ? "…" : "Near Me"}
          </button>
        </div>

        {source === "nearme" && coords && (
          <div className="flex items-center justify-center gap-1 px-1 pb-1" data-testid="feed-near-radius">
            {[5, 10, 25, 50].map((r) => (
              <button
                key={r}
                onClick={() => setRadius(r)}
                className={`px-3 h-7 rounded-full text-[11px] font-bold transition-colors ${
                  radius === r ? "bg-cyan-400 text-black" : "text-cyan-100/80 hover:text-white"
                }`}
                data-testid={`feed-near-radius-${r}`}
              >
                {r}km
              </button>
            ))}
          </div>
        )}

        {source !== "nearme" && <LiveRail />}

        <StoriesStrip />
      </div>

      {empty ? (
        <div className="min-h-[100dvh] grid place-items-center px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <h2 className="text-3xl font-black tracking-tighter">
              {source === "following"
                ? "Follow some gems first."
                : source === "nearme"
                ? "No gems near you yet."
                : "No gems yet."}
            </h2>
            <p className="mt-2 text-zinc-400 max-w-sm mx-auto">
              {source === "following"
                ? "Tap the + on any vendor's avatar to follow them. Their drops show up here."
                : source === "nearme"
                ? "Try a larger radius, or check back as new vendors pin their shops."
                : "Be the first. Become a vendor and post your first product."}
            </p>
            <div className="mt-6 flex gap-3 justify-center">
              {source === "following" ? (
                <button
                  onClick={() => setSource("foryou")}
                  className="h-12 px-6 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
                  data-testid="empty-feed-foryou-btn"
                >
                  Discover gems
                </button>
              ) : (
                <button
                  onClick={() => navigate("/sell")}
                  className="h-12 px-6 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
                  data-testid="empty-feed-sell-btn"
                >
                  Start selling
                </button>
              )}
              <button
                onClick={() => load()}
                className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-white/5 border border-white/10 text-white font-semibold"
                data-testid="empty-feed-reload-btn"
              >
                <RotateCw className="w-4 h-4" /> Reload
              </button>
            </div>
          </motion.div>
        </div>
      ) : (
        <div ref={containerRef} className="snap-feed">
          {items.map((p) => (
            <ProductSlide
              key={p.product_id}
              product={p}
              isVisible={visibleId === p.product_id}
              liveStream={liveByVendor[p.vendor_id]}
              onLike={() => onLike(p)}
              onAdd={() => onAdd(p)}
              onShare={() => onShare(p)}
              onSave={() => onSave(p)}
              onComment={() => onComment(p)}
              onFollow={() => onFollow(p)}
              onOpenVendor={() => navigate(`/vendor/${p.vendor_id}`)}
              onDoubleTapLike={() => { if (!p.liked) onLike(p); }}
            />
          ))}
        </div>
      )}

      <CommentsSheet
        product={commentsFor}
        open={!!commentsFor}
        onOpenChange={(o) => !o && setCommentsFor(null)}
        onCountChange={(count) =>
          setItems((arr) => arr.map((it) => (it.product_id === commentsFor?.product_id ? { ...it, comment_count: count } : it)))
        }
      />
    </div>
  );
}
